import { scrollDashboardToTop } from "../../../hooks/useScrollOnTabChange";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Alert } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  CmpNewsletterAssetUploadMethodEnum,
  CmpNewsletterAssetUploadPurposeEnum,
  useCreateCmpNewsletterAttachmentUploadMutation,
  useGetAllCategoriesQuery,
} from "../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../hooks/useGetChamberFromUserIdInAuth";
import { RoutePaths } from "../../../enumStrings";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNoun } from "../../../utils/cmpOrganizationNoun";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  UserFacingError,
} from "../../../utils/userFacingError";
import { enterFieldMessage } from "../../../utils/fieldMessages";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../utils/apolloCallerContext";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";
import { ActionSuccessDialog } from "../../common/dashboard/ActionSuccessDialog";
import { ErrorBanner } from "../../common/dashboard/ErrorBanner";
import { FormPageShell } from "../../common/dashboard/FormPageShell";
import { StepProgress } from "../../common/dashboard/StepProgress";
import { usePageHeaderOverride } from "../../Layout/PageContainer";
import {
  buildSlugFromSubject,
  EMAIL_CHAMBER_LOADING_MESSAGE,
  emailSchedulePartsToUtcMilli,
  formatAttachmentFileSize,
  getEmailAudienceSelectionKey,
  splitDirectRecipients,
  withLastSavedScheduleIfInvalid,
  withTypedScheduleIfInvalid,
} from "./emailBackendMappers";
import { buildEmailScheduledSuccessMessage } from "./emailManageActions";
import { refreshNewMembersBlocks } from "./emailNewMembersBlock";
import {
  emailAssetUploadApolloContext,
  isEmailImageFile,
  prepareEmailAssetForUpload,
} from "./emailAssetUpload";
import {
  NewsletterTestSendPreview,
  NewsletterTestSendResult,
} from "./emailTestSend";
import {
  UpdateEmailBuilderState,
  cloneEmailBuilderState,
} from "./emailBuilderData";
import {
  EmailAttachment,
  EmailAudienceMemberOption,
  EmailFormState,
  RecipientGroup,
  RecipientPreviewSummary,
  RecipientSourceId,
  buildDefaultNewsletterMemberFilters,
  cloneEmailForm,
} from "./emailMockData";
import { hasUnsavedEmailFormChanges } from "./emailFormDirtyState";
import {
  EmailDesignScope,
  darkButtonClassName,
  AudienceFilterId,
  EmailFormMode,
  buildAudienceSummaryRows,
  getFilteredMemberIds,
  isAllowedEmailAttachment,
  maxAttachmentBytes,
  maxAttachmentCount,
  maxTotalAttachmentBytes,
  memberMatchesRecipientSource,
  pageDescriptionClassName,
  pageTitleClassName,
  primaryButtonClassName,
  recipientCountRequiresBackendPreview,
  resolveIntendedRecipientCount,
  secondaryButtonClassName,
} from "./emailWizardShared";
import StepOneAudience from "./StepOneAudience";
import StepTwoDetails from "./StepTwoDetails";
import StepThreeContent from "./StepThreeContent";
import StepFourReview, { formatNewsroomStamp } from "./StepFourReview";

type EmailFormProps = {
  mode: EmailFormMode;
  initialStep?: number;
  showDraftSavedOnLoad?: boolean;
  initialEmail: EmailFormState;
  timeZone?: string | null;
  recipientGroups: RecipientGroup[];
  memberOptions?: EmailAudienceMemberOption[];
  memberOptionsLoading?: boolean;
  senderEmailOptions?: readonly string[];
  isLoading?: boolean;
  isSaving?: boolean;
  onSubmit: (
    email: EmailFormState,
    options?: { stayOnCurrentPage?: boolean }
  ) => Promise<EmailFormState | void>;
  onArchiveRecipientGroup: (groupId: string) => Promise<void>;
  onPreviewRecipients: (
    email: EmailFormState
  ) => Promise<RecipientPreviewSummary | null>;
  onDuplicateAsDraft?: (email: EmailFormState) => Promise<void>;
  onPublishNewsroom?: (email: EmailFormState) => Promise<EmailFormState | void>;
  onUnpublishNewsroom?: (
    email: EmailFormState
  ) => Promise<EmailFormState | void>;
  // F2 / design (16) "Update Published Email": a Sent email saves only its
  // newsroom article (title, summary, body, preview image).
  onUpdatePublishedNewsroom?: (email: EmailFormState) => Promise<EmailFormState>;
  onPreviewTestSend?: (
    email: EmailFormState,
    recipientEmails: string[],
    clientRequestId: string
  ) => Promise<NewsletterTestSendPreview>;
  onSendReview?: (
    email: EmailFormState,
    recipientEmails: string[],
    clientRequestId: string,
    confirmationToken: string
  ) => Promise<NewsletterTestSendResult & { email?: EmailFormState | null }>;
  onSendNow?: (email: EmailFormState) => Promise<EmailFormState | void>;
  onSendSuccess: () => void;
  onSchedule?: (email: EmailFormState) => Promise<EmailFormState | void>;
  onCancelSchedule?: (email: EmailFormState) => Promise<EmailFormState | void>;
};

type WizardStep = 1 | 2 | 3 | 4;
type BuilderSnapshot = Pick<
  EmailFormState,
  "builderState" | "templateId" | "emailType"
>;
const builderSnapshot = (email: BuilderSnapshot): BuilderSnapshot => ({
  builderState: cloneEmailBuilderState(email.builderState),
  templateId: email.templateId,
  emailType: email.emailType,
});
const builderSnapshotKey = (email: BuilderSnapshot) => {
  const content = { ...email.builderState, selectedBlockId: null };
  return JSON.stringify({
    builderState: content,
    templateId: email.templateId,
    emailType: email.emailType,
  });
};
const mergeNewsroomMetadata = (
  current: EmailFormState,
  saved: EmailFormState
): EmailFormState => ({
  ...current,
  publishToNewsroom: saved.publishToNewsroom,
  newsroomStatus: saved.newsroomStatus,
  newsroomSlug: saved.newsroomSlug,
  newsroomImageUrl: saved.newsroomImageUrl,
  newsroomPublishedAtUtcMilli: saved.newsroomPublishedAtUtcMilli,
  newsroomUnpublishedAtUtcMilli: saved.newsroomUnpublishedAtUtcMilli,
  seoTitle: saved.seoTitle,
  seoDescription: saved.seoDescription,
});
export const getInitialEmailWizardStep = (value?: number): WizardStep =>
  value === 2 || value === 3 || value === 4 ? value : 1;
// Inline notices are for failures and warnings only. Every completed action
// uses the shared success pop-up (design ActionSuccessModal).
type NoticeState = {
  severity: "info" | "warning" | "error";
  message: string;
} | null;

type SuccessState = { message: string; highlights?: string[] } | null;

const draftSavedSuccess: SuccessState = {
  message: "Your email draft has been saved successfully!",
};

type AudiencePreviewResult = {
  selectionKey: string;
  summary: RecipientPreviewSummary;
};

type AudiencePreviewError = {
  selectionKey: string;
  message: string;
};

const audiencePreviewDebounceMilliseconds = 400;
const sendSuccessRedirectDelayMilliseconds = 3000;

const steps: Array<{ id: WizardStep; title: string }> = [
  // D@1740588 (EC-3, Monday D22): step 1 is "Select Audience".
  { id: 1, title: "Select Audience" },
  { id: 2, title: "Campaign Details" },
  { id: 3, title: "Content Builder" },
  { id: 4, title: "Review & Send" },
];

const EmailForm = ({
  mode,
  initialStep,
  showDraftSavedOnLoad = false,
  initialEmail,
  timeZone,
  recipientGroups,
  memberOptions = [],
  memberOptionsLoading = false,
  senderEmailOptions = [],
  isLoading = false,
  isSaving = false,
  onSubmit,
  onArchiveRecipientGroup,
  onPreviewRecipients,
  onDuplicateAsDraft,
  onPublishNewsroom,
  onUnpublishNewsroom,
  onUpdatePublishedNewsroom,
  onPreviewTestSend,
  onSendReview,
  onSendNow,
  onSendSuccess,
  onSchedule,
  onCancelSchedule,
}: EmailFormProps) => {
  const setPageHeaderOverride = usePageHeaderOverride();
  const noun = useCmpOrganizationNoun();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [createCmpNewsletterAttachmentUpload] =
    useCreateCmpNewsletterAttachmentUploadMutation({
      context: emailAssetUploadApolloContext,
    });
  // Categories only name the industry rows in the audience summary. A failure
  // stays on this page as a warning instead of opening the error page.
  const { data: categoriesData, error: categoriesError } =
    useGetAllCategoriesQuery({ context: CALLER_HANDLED_USER_ERROR_CONTEXT });
  const [email, setEmail] = useState<EmailFormState>(
    cloneEmailForm(initialEmail)
  );
  const [lastSavedEmail, setLastSavedEmail] = useState<EmailFormState>(
    cloneEmailForm(initialEmail)
  );
  const [step, setStep] = useState<WizardStep>(() =>
    getInitialEmailWizardStep(initialStep)
  );
  const [notice, setNotice] = useState<NoticeState>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [localActionLoading, setLocalActionLoading] = useState(false);
  const [sendNowLoading, setSendNowLoading] = useState(false);
  const [sendSuccessOpen, setSendSuccessOpen] = useState(false);
  const [activeAssetUploadCount, setActiveAssetUploadCount] = useState(0);
  const [audiencePreviewResult, setAudiencePreviewResult] =
    useState<AudiencePreviewResult | null>(null);
  const [audiencePreviewError, setAudiencePreviewError] =
    useState<AudiencePreviewError | null>(null);
  const [pendingAudiencePreviewKey, setPendingAudiencePreviewKey] = useState<
    string | null
  >(null);
  const latestAudiencePreviewRequestIdRef = useRef(0);
  const preparedTestSendRef = useRef<{
    clientRequestId: string;
    confirmationToken: string;
    recipientEmails: string[];
    savedEmail: EmailFormState;
  } | null>(null);
  // Create Email mounts before the authenticated chamber query has always
  // resolved. Include chamber identity so generated chamber defaults replace
  // temporary placeholders before the form unlocks.
  const initialEmailResetKey = `${mode}:${initialEmail.id || "create"}:${
    chamberId || "loading"
  }`;
  const loadedEmailResetKeyRef = useRef(initialEmailResetKey);
  const latestEmailRef = useRef(email);
  const builderPastRef = useRef<BuilderSnapshot[]>([]);
  const builderFutureRef = useRef<BuilderSnapshot[]>([]);
  const builderTransactionRef = useRef<BuilderSnapshot | null>(null);
  const [, setHistoryRevision] = useState(0);
  const [audienceRefreshRevision, setAudienceRefreshRevision] = useState(0);
  const [success, setSuccess] = useState<SuccessState>(
    showDraftSavedOnLoad ? draftSavedSuccess : null
  );
  const showSuccess = useCallback((next: SuccessState) => {
    setNotice(null);
    setSuccess(next);
  }, []);
  const onPreviewRecipientsRef = useRef(onPreviewRecipients);
  latestEmailRef.current = email;
  onPreviewRecipientsRef.current = onPreviewRecipients;

  const handleSendSuccessRedirect = useCallback(() => {
    setSendSuccessOpen(false);
    onSendSuccess();
  }, [onSendSuccess]);

  useEffect(() => {
    if (!sendSuccessOpen) return undefined;

    const redirectTimer = window.setTimeout(
      handleSendSuccessRedirect,
      sendSuccessRedirectDelayMilliseconds
    );

    return () => window.clearTimeout(redirectTimer);
  }, [handleSendSuccessRedirect, sendSuccessOpen]);

  useEffect(() => {
    if (loadedEmailResetKeyRef.current === initialEmailResetKey) return;
    loadedEmailResetKeyRef.current = initialEmailResetKey;
    setEmail(cloneEmailForm(initialEmail));
    setLastSavedEmail(cloneEmailForm(initialEmail));
    setAudiencePreviewResult(null);
    setAudiencePreviewError(null);
    setPendingAudiencePreviewKey(null);
    setLocalActionLoading(false);
    setErrors({});
    setStep(getInitialEmailWizardStep(initialStep));
    builderPastRef.current = [];
    builderFutureRef.current = [];
    builderTransactionRef.current = null;
    setHistoryRevision((revision) => revision + 1);
  }, [initialEmail, initialEmailResetKey, initialStep]);

  const isBusy =
    isLoading || isSaving || localActionLoading || activeAssetUploadCount > 0;
  useEffect(() => {
    if (timeZone !== undefined) setEmail(current => ({ ...current, timeZone: timeZone || "" }));
  }, [timeZone]);
  const isSentRecord = email.status === "sent" || email.status === "sending";
  const hasUnsavedChanges = useMemo(
    () =>
      hasUnsavedEmailFormChanges({
        baseline: lastSavedEmail,
        current: email,
      }),
    [email, lastSavedEmail]
  );
  const directRecipientCount = splitDirectRecipients(
    email.directRecipients
  ).length;
  const groupRecipientCount = recipientGroups
    .filter((group) => email.recipientGroupIds.includes(group.id))
    .reduce((total, group) => total + group.count, 0);
  const audienceSelectionKey = getEmailAudienceSelectionKey(email);
  const previewSummary =
    audiencePreviewResult?.selectionKey === audienceSelectionKey
      ? audiencePreviewResult.summary
      : null;
  const currentAudiencePreviewError =
    audiencePreviewError?.selectionKey === audienceSelectionKey
      ? applyCmpOrganizationNoun(audiencePreviewError.message, noun)
      : "";
  const isAudiencePreviewLoading =
    pendingAudiencePreviewKey === audienceSelectionKey;
  // Live local estimate so "Estimated Recipients" reacts to audience/source/filter
  // changes before the backend preview runs (backend preview stays the truth).
  const localEstimatedMemberCount = useMemo(() => {
    const filterSet = new Set(
      getFilteredMemberIds({
        filters: email.memberFilters,
        memberOptions,
      })
    );
    const ids = new Set<string>();

    memberOptions
      .filter(
        (member) =>
          filterSet.has(member.id) &&
          email.recipientSourceIds.some((sourceId) =>
            memberMatchesRecipientSource({ member, sourceId })
          )
      )
      .forEach((member) => ids.add(member.id));

    // Explicitly selected members remain a fixed audience and are not narrowed
    // by continuously managed member filters.
    email.selectedMemberIds.forEach((memberId) => ids.add(memberId));
    return ids.size;
  }, [
    email.memberFilters,
    email.recipientSourceIds,
    email.selectedMemberIds,
    memberOptions,
  ]);
  const hasRecipients =
    email.recipientSourceIds.filter(
      (sourceId) => sourceId !== "selectedMembers"
    ).length > 0 ||
    email.recipientGroupIds.length > 0 ||
    email.selectedMemberIds.length > 0 ||
    directRecipientCount > 0;
  const intendedRecipientCount = resolveIntendedRecipientCount({
    fallbackCount:
      localEstimatedMemberCount + groupRecipientCount + directRecipientCount,
    hasRecipients,
    isSentRecord,
    previewCount: previewSummary?.uniqueValidRecipientCount,
    requiresBackendPreview: recipientCountRequiresBackendPreview(
      email.recipientSourceIds
    ),
    storedCount: email.estimatedRecipientCount,
  });

  useEffect(() => {
    const requestId = latestAudiencePreviewRequestIdRef.current + 1;
    latestAudiencePreviewRequestIdRef.current = requestId;

    if (!hasRecipients || isLoading || isSentRecord) {
      setPendingAudiencePreviewKey(null);
      setAudiencePreviewError(null);
      if (!hasRecipients) setAudiencePreviewResult(null);
      return;
    }

    const emailSnapshot = cloneEmailForm(latestEmailRef.current);
    setPendingAudiencePreviewKey(audienceSelectionKey);
    setAudiencePreviewError(null);

    const timeoutId = window.setTimeout(async () => {
      try {
        const summary = await onPreviewRecipientsRef.current(emailSnapshot);
        if (latestAudiencePreviewRequestIdRef.current !== requestId) return;
        if (!summary) {
          throw new Error("Recipient preview did not return a summary.");
        }
        setAudiencePreviewResult({
          selectionKey: audienceSelectionKey,
          summary,
        });
        setAudiencePreviewError(null);
      } catch (error) {
        if (latestAudiencePreviewRequestIdRef.current !== requestId) return;
        setAudiencePreviewError({
          selectionKey: audienceSelectionKey,
          message: getUserFacingErrorText(
            error,
            buildStaffErrorFallback(
              "count the recipients for this audience",
              "Check your internet connection, then refresh the page."
            )
          ),
        });
      } finally {
        if (latestAudiencePreviewRequestIdRef.current === requestId) {
          setPendingAudiencePreviewKey(null);
        }
      }
    }, audiencePreviewDebounceMilliseconds);

    return () => {
      window.clearTimeout(timeoutId);
      if (latestAudiencePreviewRequestIdRef.current === requestId) {
        // Invalidate an in-flight request so a slower, older selection can
        // never overwrite the current audience preview.
        latestAudiencePreviewRequestIdRef.current += 1;
      }
    };
  }, [
    audienceSelectionKey,
    audienceRefreshRevision,
    hasRecipients,
    isLoading,
    isSentRecord,
  ]);

  const hasScheduleParts = !!email.scheduledDate && !!email.scheduledTime;
  // C3: "Welcome Our New Members" blocks refresh from the loaded member list
  // before a send, schedule, or test send saves the content, and in the
  // Review & Send preview. Sent records are never changed.
  const withCurrentNewMembers = useCallback(
    (source: EmailFormState): EmailFormState => {
      if (source.status === "sent" || source.status === "sending") return source;
      const builderState = refreshNewMembersBlocks({
        builderState: source.builderState,
        members: memberOptions,
        membersReady: !memberOptionsLoading,
      });
      return builderState === source.builderState
        ? source
        : { ...source, builderState };
    },
    [memberOptions, memberOptionsLoading]
  );
  const reviewEmail = useMemo(
    () => withCurrentNewMembers(email),
    [email, withCurrentNewMembers]
  );
  const canRunSavedBackendAction = !!email.id;
  const memberTypeOptions = useMemo(() => {
    const membershipTypeNamesById = new Map<string, string>();
    memberOptions.forEach((member) => {
      if (member.membershipTypeId && member.membershipTypeName) {
        membershipTypeNamesById.set(
          member.membershipTypeId,
          member.membershipTypeName
        );
      }
    });
    return Array.from(membershipTypeNamesById.entries()).map(([id, label]) => ({
      id,
      label,
    }));
  }, [memberOptions]);
  const industryLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    (categoriesData?.getAllCategories || []).forEach((category) => {
      labels[category.id] = category.name;
    });
    memberTypeOptions.forEach((membershipType) => {
      labels[`membershipType:${membershipType.id}`] = membershipType.label;
    });
    return labels;
  }, [categoriesData?.getAllCategories, memberTypeOptions]);
  const audienceSummaryRows = useMemo(
    () =>
      buildAudienceSummaryRows({
        email,
        recipientGroups,
        memberOptions,
        audienceFilters: email.memberFilters,
        industryLabels,
      }),
    [email, industryLabels, memberOptions, recipientGroups]
  );

  const recordBuilderChange = useCallback(
    (before: BuilderSnapshot, after: BuilderSnapshot) => {
      if (
        builderTransactionRef.current ||
        builderSnapshotKey(before) === builderSnapshotKey(after)
      )
        return;
      builderPastRef.current = [
        ...builderPastRef.current,
        builderSnapshot(before),
      ].slice(-50);
      builderFutureRef.current = [];
      setHistoryRevision((revision) => revision + 1);
    },
    []
  );
  const updateEmailField = <FieldName extends keyof EmailFormState>(
    field: FieldName,
    value: EmailFormState[FieldName]
  ) => {
    const current = latestEmailRef.current;
    const next = { ...current, [field]: value };
    if (field === "templateId" || field === "emailType")
      recordBuilderChange(current, next);
    latestEmailRef.current = next;
    setEmail(next);
  };
  const updateBuilderState: UpdateEmailBuilderState = useCallback(
    (update) => {
      const current = latestEmailRef.current;
      const next = {
        ...current,
        builderState:
          typeof update === "function" ? update(current.builderState) : update,
      };
      recordBuilderChange(current, next);
      latestEmailRef.current = next;
      setEmail(next);
    },
    [recordBuilderChange]
  );
  const restoreBuilder = useCallback((snapshot: BuilderSnapshot) => {
    const next = { ...latestEmailRef.current, ...builderSnapshot(snapshot) };
    latestEmailRef.current = next;
    setEmail(next);
    setHistoryRevision((revision) => revision + 1);
  }, []);
  const startBuilderTransaction = () => {
    if (!builderTransactionRef.current)
      builderTransactionRef.current = builderSnapshot(latestEmailRef.current);
  };
  const endBuilderTransaction = (cancel = false) => {
    const before = builderTransactionRef.current;
    builderTransactionRef.current = null;
    if (!before) return;
    if (cancel) restoreBuilder(before);
    else recordBuilderChange(before, latestEmailRef.current);
  };
  const undoBuilder = () => {
    if (isBusy || isSentRecord || builderTransactionRef.current) return;
    const previous = builderPastRef.current.pop();
    if (!previous) return;
    builderFutureRef.current = [
      ...builderFutureRef.current,
      builderSnapshot(latestEmailRef.current),
    ].slice(-50);
    restoreBuilder(previous);
  };
  const redoBuilder = () => {
    if (isBusy || isSentRecord || builderTransactionRef.current) return;
    const next = builderFutureRef.current.pop();
    if (!next) return;
    builderPastRef.current = [
      ...builderPastRef.current,
      builderSnapshot(latestEmailRef.current),
    ].slice(-50);
    restoreBuilder(next);
  };

  // Every email asset uses one upload boundary. Image files are rasterized,
  // resized, metadata-stripped, and converted to PNG/JPEG before the backend
  // routes them to an isolated chamber prefix in the existing media bucket.
  const uploadEmailAsset = useCallback(
    async (
      file: File,
      purpose: CmpNewsletterAssetUploadPurposeEnum,
      validatePreparedFile?: (preparedFile: File) => void
    ) => {
      if (!chamberId) {
        throw new UserFacingError(
          applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun)
        );
      }

      setActiveAssetUploadCount((currentCount) => currentCount + 1);
      try {
        const uploadFile = await prepareEmailAssetForUpload({ file });
        validatePreparedFile?.(uploadFile);
        const result = await createCmpNewsletterAttachmentUpload({
          variables: {
            input: {
              chamberId,
              purpose,
              fileName: uploadFile.name,
              mimeType: uploadFile.type,
              sizeBytes: uploadFile.size,
            },
          },
        });
        const upload = result.data?.createCmpNewsletterAttachmentUpload;
        if (!upload) {
          throw new Error("The email asset upload could not be prepared.");
        }
        if (upload.uploadMethod !== CmpNewsletterAssetUploadMethodEnum.Put) {
          throw new Error("The email asset upload method is not supported.");
        }
        const uploadResponse = await fetch(upload.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": uploadFile.type },
          body: uploadFile,
        });
        if (!uploadResponse.ok) {
          throw new Error("The email asset could not be uploaded.");
        }
        return upload;
      } finally {
        setActiveAssetUploadCount((currentCount) =>
          Math.max(currentCount - 1, 0)
        );
      }
    },
    [chamberId, createCmpNewsletterAttachmentUpload, noun]
  );

  const uploadBuilderImage = useCallback(
    async (file: File): Promise<string> => {
      const upload = await uploadEmailAsset(
        file,
        CmpNewsletterAssetUploadPurposeEnum.BuilderImage
      );
      if (!upload.publicUrl) {
        throw new Error("The content image URL could not be prepared.");
      }
      return upload.publicUrl;
    },
    [uploadEmailAsset]
  );

  const uploadBuilderPdf = useCallback(
    async (file: File): Promise<string> => {
      // Builder PDFs become public button destinations. They are intentionally
      // separate from MIME attachments, whose storage is not exposed as a URL.
      const upload = await uploadEmailAsset(
        file,
        CmpNewsletterAssetUploadPurposeEnum.BuilderPdf
      );
      if (!upload.publicUrl) {
        throw new Error("The content PDF URL could not be prepared.");
      }
      return upload.publicUrl;
    },
    [uploadEmailAsset]
  );

  const updateAudienceFilter = (
    filterId: AudienceFilterId,
    values: string[]
  ) => {
    setEmail((currentEmail) => ({
      ...currentEmail,
      memberFilters: {
        ...currentEmail.memberFilters,
        [filterId]: values,
      },
    }));
  };

  const clearAllFilters = () => {
    setEmail((currentEmail) => ({
      ...currentEmail,
      memberFilters: buildDefaultNewsletterMemberFilters(),
    }));
  };

  const toggleRecipientSource = (sourceId: RecipientSourceId) => {
    setEmail((currentEmail) => {
      const removing = currentEmail.recipientSourceIds.includes(sourceId);
      return {
        ...currentEmail,
        recipientSourceIds: removing
          ? currentEmail.recipientSourceIds.filter((id) => id !== sourceId)
          : [...currentEmail.recipientSourceIds, sourceId],
        // Each picker list belongs only to its own audience.
        ...(removing && sourceId === "eventAttendees" ? { eventIds: [] } : {}),
        ...(removing && sourceId === "eventSponsors" ? { sponsorEventIds: [] } : {}),
        ...(removing && sourceId === "governanceGroups" ? { governanceGroupIds: [] } : {}),
      };
    });
  };

  const toggleRecipientGroup = (groupId: string) => {
    setEmail((currentEmail) => ({
      ...currentEmail,
      recipientGroupIds: currentEmail.recipientGroupIds.includes(groupId)
        ? currentEmail.recipientGroupIds.filter((id) => id !== groupId)
        : [...currentEmail.recipientGroupIds, groupId],
    }));
  };

  // The buttons sit far below the alert, so show the error the person caused.
  useEffect(() => {
    if (notice?.severity === "error") {
      noticeRef.current?.scrollIntoView?.({ block: "center" });
    }
  }, [notice]);

  const runAction = useCallback(
    async (
      action: () => Promise<EmailFormState | void>,
      successState: SuccessState
    ) => {
      const requestResetKey = loadedEmailResetKeyRef.current;
      setLocalActionLoading(true);
      try {
        const savedEmail = await action();
        if (loadedEmailResetKeyRef.current !== requestResetKey) return null;
        const nextEmail = savedEmail
          ? cloneEmailForm(savedEmail)
          : cloneEmailForm(email);
        setEmail(nextEmail);
        setLastSavedEmail(nextEmail);
        if (successState) showSuccess(successState);
        else setNotice(null);
        return nextEmail;
      } catch (error) {
        if (loadedEmailResetKeyRef.current === requestResetKey) {
          setNotice({
            severity: "error",
            message: getUserFacingErrorText(
              error,
              buildStaffErrorFallback("save your email")
            ),
          });
        }
        return null;
      } finally {
        if (loadedEmailResetKeyRef.current === requestResetKey) {
          setLocalActionLoading(false);
        }
      }
    },
    [email, showSuccess]
  );

  const saveDraft = useCallback(async (showConfirmation = true) => {
    // Read the current form ref at click time so a save cannot submit the
    // pre-builder snapshot after blocks change.
    const emailToSave = cloneEmailForm(latestEmailRef.current);
    const saved = await runAction(
      () => onSubmit(emailToSave),
      showConfirmation ? draftSavedSuccess : null
    );
    return Boolean(saved);
  }, [onSubmit, runAction]);

  const runMetadataAction = async ({
    action,
    mergeMetadata,
    successMessage,
    failureTask,
  }: {
    action: (snapshot: EmailFormState) => Promise<EmailFormState | void>;
    /** Task for the error text, for example "publish this email to your newsroom". */
    failureTask: string;
    mergeMetadata: (
      current: EmailFormState,
      saved: EmailFormState
    ) => EmailFormState;
    successMessage: string | ((saved: EmailFormState) => NonNullable<SuccessState>);
  }) => {
    if (isBusy) return;
    const requestResetKey = loadedEmailResetKeyRef.current;
    const requestEmail = cloneEmailForm(latestEmailRef.current);
    setLocalActionLoading(true);
    try {
      const savedEmail = await action(requestEmail);
      if (loadedEmailResetKeyRef.current !== requestResetKey) return;
      if (!savedEmail || savedEmail.id !== requestEmail.id) {
        throw new UserFacingError(
          "We could not confirm that your change was saved. Open Manage Emails & Communications to check the email, then try again."
        );
      }
      // These actions do not save content. Merge into the latest local form
      // and its existing baseline so pending edits remain unsaved and visible.
      const nextEmail = mergeMetadata(latestEmailRef.current, savedEmail);
      latestEmailRef.current = nextEmail;
      setEmail(nextEmail);
      setLastSavedEmail((baseline) => mergeMetadata(baseline, savedEmail));
      showSuccess(typeof successMessage === "string" ? { message: successMessage } : successMessage(nextEmail));
    } catch (error) {
      if (loadedEmailResetKeyRef.current === requestResetKey) {
        setNotice({
          severity: "error",
          message: getUserFacingErrorText(
            error,
            buildStaffErrorFallback(failureTask)
          ),
        });
      }
    } finally {
      if (loadedEmailResetKeyRef.current === requestResetKey) {
        setLocalActionLoading(false);
      }
    }
  };

  const builderBackRef = useRef<(() => void) | null>(null);
  const onBuilderBackChange = useCallback((back: (() => void) | null) => { builderBackRef.current = back; }, []);
  // Design (D:1277-1280): edit mode reads "Edit Communication" and "Edit and send...".
  const pageTitle = mode === "create" ? "Create Email" : "Edit Communication";
  const pageDescription = `${mode === "create" ? "Create" : "Edit"} and send targeted ${noun.singular} communications to the right audience, increasing email open and click-through rates with professional email templates and advanced member filtering.`;

  useEffect(() => {
    setPageHeaderOverride({
      title: pageTitle,
      description: pageDescription,
      titleClassName: pageTitleClassName.replace("font-semibold", "font-bold"),
      descriptionClassName: pageDescriptionClassName.replace("mt-2", "mt-[6px]"),
      // The visible Emails header is grouped with the stepper below. Hide the
      // route shell's separate static header so there is one desktop sticky block.
      headerSpacingClassName: "hidden",
      horizontalPaddingClassName: "px-4 md:px-8",
    });

    return () => setPageHeaderOverride(null);
  }, [pageTitle, pageDescription, setPageHeaderOverride]);

  const validateStep = (stepToValidate: WizardStep) => {
    const nextErrors: Record<string, string> = {};

    if (stepToValidate === 1 && !hasRecipients) {
      nextErrors.recipients = "Select at least one audience before continuing.";
    }
    if (
      stepToValidate === 1 &&
      email.recipientSourceIds.includes("eventAttendees") &&
      email.eventIds.length === 0
    ) {
      nextErrors.eventIds = "Select at least one event for Event Attendees.";
    }
    if (
      stepToValidate === 1 &&
      email.recipientSourceIds.includes("governanceGroups") &&
      email.governanceGroupIds.length === 0
    ) {
      nextErrors.governanceGroupIds = "Select at least one group for Governance and Groups.";
    }
    if (
      stepToValidate === 1 &&
      email.recipientSourceIds.includes("eventSponsors") &&
      email.sponsorEventIds.length === 0
    ) {
      nextErrors.sponsorEventIds = "Select at least one event for Sponsors.";
    }
    if (stepToValidate === 2) {
      if (!email.campaignName.trim())
        nextErrors.campaignName = enterFieldMessage("Campaign Name");
      if (!email.subject.trim())
        nextErrors.subject = enterFieldMessage("Email Subject Line");
      if (!email.fromName.trim())
        nextErrors.fromName = enterFieldMessage("From Name");
    }
    // Step 3 is complete once a template's blocks were loaded onto the canvas
    // OR the custom email canvas has at least one block (builder-shell spec).
    if (stepToValidate === 3 && email.builderState.blocks.length === 0 && !email.builderState.preservedDocument?.contentHtml && !email.builderState.preservedDocument?.contentText) {
      nextErrors.content =
        "Select an email template or add at least one content block before continuing.";
    }
    // Design (16) goEmailStep4 (EC-32): a video block needs its Video URL.
    if (
      stepToValidate === 3 &&
      email.builderState.blocks.some(
        (block) => block.type === "video" && !block.settings.linkUrl.trim(),
      )
    ) {
      nextErrors.video =
        "Please add a Video URL to the video block before continuing.";
    }

    return nextErrors;
  };

  const validateSenderBeforeSending = (): string | null => {
    const sender = email.fromEmail.trim().toLowerCase();
    const message = senderEmailOptions.length === 0
      ? `No sender email is set up for your ${noun.singular}, so this email cannot be sent. Add a sender email in ${noun.Singular} Set Up, then try again.`
      : !sender
        ? enterFieldMessage("From Email Address")
        : !senderEmailOptions.includes(sender)
          ? `${email.fromEmail.trim()} is not set up as a sender email. Enter a sender email that is set up in ${noun.Singular} Set Up, or add this address there first.`
          : null;
    if (message) {
      setErrors({ fromEmail: message });
      setStep(2);
      scrollDashboardToTop();
    }
    return message;
  };

  const goToStep = (nextStep: WizardStep) => {
    if (nextStep > step) {
      // Validate every step between the current one and the target so a
      // stepper jump cannot skip a required step (for example landing on
      // Review & Send while the Content Builder canvas is still empty).
      for (let stepId = step; stepId < nextStep; stepId += 1) {
        const stepErrors = validateStep(stepId as WizardStep);
        if (Object.keys(stepErrors).length > 0) {
          setErrors(stepErrors);
          setStep(stepId as WizardStep);
          scrollDashboardToTop();
          return;
        }
      }
    }

    // Sender setup must not block draft content work, including reopened drafts.
    if (nextStep === 4 && !isSentRecord && validateSenderBeforeSending()) return;

    setErrors({});
    setStep(nextStep);
    scrollDashboardToTop();
  };

  // Step 1 shows the failure in its own error pop-up, so a failure is
  // rethrown here instead of adding a second inline message.
  const archiveRecipientGroup = async (group: RecipientGroup) => {
    setLocalActionLoading(true);
    try {
      await onArchiveRecipientGroup(group.id);
      setEmail((currentEmail) => ({
        ...currentEmail,
        recipientGroupIds: currentEmail.recipientGroupIds.filter(
          (groupId) => groupId !== group.id
        ),
      }));
      // Design copy; the backend archives the custom audience.
      showSuccess({ message: `Custom audience "${group.name}" has been deleted.` });
    } finally {
      setLocalActionLoading(false);
    }
  };

  const addAttachment = async (file?: File) => {
    if (!file) return;
    if (email.attachments.length >= maxAttachmentCount) {
      setNotice({
        severity: "error",
        message: `You can add up to ${maxAttachmentCount} attachments to an email. Remove one to add another.`,
      });
      return;
    }
    if (!isAllowedEmailAttachment({ file })) {
      setNotice({
        severity: "error",
        message:
          "This file type cannot be attached. Choose a PDF, Word (DOC or DOCX) or image file.",
      });
      return;
    }
    if (!isEmailImageFile({ file }) && file.size > maxAttachmentBytes) {
      setNotice({
        severity: "error",
        message: "Attachments must be 6MB or smaller.",
      });
      return;
    }
    setLocalActionLoading(true);
    try {
      const currentAttachmentBytes = email.attachments.reduce(
        (totalBytes, attachment) => totalBytes + attachment.sizeBytes,
        0
      );
      const upload = await uploadEmailAsset(
        file,
        CmpNewsletterAssetUploadPurposeEnum.Attachment,
        (preparedFile) => {
          if (
            currentAttachmentBytes + preparedFile.size >
            maxTotalAttachmentBytes
          ) {
            throw new UserFacingError(
              "Combined attachments must be 6MB or smaller so the email can be sent."
            );
          }
        }
      );
      const attachment: EmailAttachment = {
        id: upload.storageKey,
        name: upload.fileName,
        fileType: upload.mimeType,
        sizeBytes: upload.sizeBytes,
        sizeLabel: formatAttachmentFileSize(upload.sizeBytes),
        storageKey: upload.storageKey,
      };
      setEmail((currentEmail) => ({
        ...currentEmail,
        attachments: [...currentEmail.attachments, attachment],
      }));
      showSuccess({ message: `${attachment.name} added.`, highlights: [attachment.name] });
    } catch (error) {
      setNotice({
        severity: "error",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            `upload ${file.name}`,
            "Check your internet connection."
          )
        ),
      });
    } finally {
      setLocalActionLoading(false);
    }
  };

  const runSavedEmailAction = async (
    action: (savedEmail: EmailFormState) => Promise<EmailFormState | void>,
    successState: SuccessState,
    failureTask: string,
    keepOnSavedEmail: (savedEmail: EmailFormState) => EmailFormState = (savedEmail) => savedEmail
  ) => {
    setLocalActionLoading(true);
    let savedEmailForState: EmailFormState | null = null;

    try {
      // A new email is created by this save, so Schedule works before the
      // first Save Draft. The saved id is reused by every later save.
      const savedEmail = await onSubmit(withCurrentNewMembers(email), {
        stayOnCurrentPage: true,
      });
      savedEmailForState = keepOnSavedEmail(
        savedEmail ? cloneEmailForm(savedEmail) : cloneEmailForm(email)
      );
      const actionEmail = await action(savedEmailForState);
      const nextEmail = actionEmail
        ? cloneEmailForm(actionEmail)
        : cloneEmailForm(savedEmailForState);
      setEmail(nextEmail);
      setLastSavedEmail(nextEmail);
      if (successState) showSuccess(successState);
      else setNotice(null);
      return nextEmail;
    } catch (error) {
      if (savedEmailForState) {
        setEmail(savedEmailForState);
        setLastSavedEmail(savedEmailForState);
      }
      setNotice({
        severity: "error",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback(failureTask)
        ),
      });
      return null;
    } finally {
      setLocalActionLoading(false);
    }
  };

  const previewTestSend = async (
    recipientEmails: string[],
    clientRequestId: string
  ): Promise<NewsletterTestSendPreview> => {
    if (!onPreviewTestSend) {
      throw new Error("Test email preview is not available.");
    }
    const senderError = validateSenderBeforeSending();
    if (senderError) throw new UserFacingError(senderError);

    setLocalActionLoading(true);
    let savedEmailForState: EmailFormState | null = null;

    try {
      // Save first (this creates a new email), then preview. The existing
      // confirmation dialog still shows before any test email is sent.
      const emailToSave = withLastSavedScheduleIfInvalid(
        withCurrentNewMembers(email),
        lastSavedEmail
      );
      const savedEmail = await onSubmit(emailToSave, {
        stayOnCurrentPage: true,
      });
      savedEmailForState = savedEmail
        ? cloneEmailForm(savedEmail)
        : cloneEmailForm(emailToSave);
      setEmail(withTypedScheduleIfInvalid(savedEmailForState, email));
      setLastSavedEmail(savedEmailForState);

      const preview = await onPreviewTestSend(
        savedEmailForState,
        recipientEmails,
        clientRequestId
      );
      if (preview.explicitRecipientsAllowed && !preview.confirmationToken) {
        throw new Error(
          "The server did not return a usable test-email confirmation."
        );
      }
      preparedTestSendRef.current = {
        clientRequestId,
        confirmationToken: preview.confirmationToken || "",
        recipientEmails: [...preview.normalizedRecipientEmails],
        savedEmail: cloneEmailForm(savedEmailForState),
      };
      return preview;
    } catch (error) {
      if (savedEmailForState) {
        setEmail(withTypedScheduleIfInvalid(savedEmailForState, email));
        setLastSavedEmail(savedEmailForState);
      }
      setNotice({
        severity: "error",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            "send the test email",
            "Check the email addresses."
          )
        ),
      });
      throw error;
    } finally {
      setLocalActionLoading(false);
    }
  };

  const sendReview = async (
    recipientEmails: string[],
    clientRequestId: string,
    confirmationToken: string
  ) => {
    if (!onSendReview) {
      throw new Error("Test email sending is not available.");
    }
    const senderError = validateSenderBeforeSending();
    if (senderError) throw new UserFacingError(senderError);

    const preparedTestSend = preparedTestSendRef.current;
    if (
      !preparedTestSend ||
      preparedTestSend.clientRequestId !== clientRequestId ||
      preparedTestSend.confirmationToken !== confirmationToken ||
      JSON.stringify(preparedTestSend.recipientEmails) !==
        JSON.stringify(recipientEmails)
    ) {
      throw new UserFacingError(
        "The test email details changed. Select Go Back, then select Send Test Email again."
      );
    }

    setLocalActionLoading(true);
    try {
      const result = await onSendReview(
        preparedTestSend.savedEmail,
        recipientEmails,
        clientRequestId,
        confirmationToken
      );
      const nextEmail = result.email
        ? cloneEmailForm(result.email)
        : cloneEmailForm(preparedTestSend.savedEmail);
      setEmail(withTypedScheduleIfInvalid(nextEmail, latestEmailRef.current));
      setLastSavedEmail(nextEmail);
      preparedTestSendRef.current = null;
      const recipients = result.actualRecipientEmails.join(", ");
      showSuccess({
        message: `A test email has been sent to ${recipients}.`,
        highlights: [recipients],
      });
    } catch (error) {
      setNotice({
        severity: "error",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            "send the test email",
            "Check the email addresses."
          )
        ),
      });
      throw error;
    } finally {
      setLocalActionLoading(false);
    }
  };

  const sendNow = async () => {
    if (!onSendNow || validateSenderBeforeSending()) return;
    setSendNowLoading(true);
    try {
      // The send pop-up below is the single success confirmation.
      const sentEmail = await runSavedEmailAction(
        (savedEmail) => onSendNow(savedEmail),
        null,
        "send your email"
      );
      if (sentEmail) {
        setSendSuccessOpen(true);
      }
    } finally {
      setSendNowLoading(false);
    }
  };

  const scheduleEmail = async () => {
    if (!onSchedule || validateSenderBeforeSending()) return;
    if (!hasScheduleParts) {
      setNotice({
        severity: "warning",
        message: "Choose a Scheduled Date and Scheduled Time.",
      });
      return;
    }
    const scheduledAtUtcMilli = emailSchedulePartsToUtcMilli(
      email.scheduledDate,
      email.scheduledTime,
      email.timeZone
    );
    const name = email.campaignName.trim() || "Your email campaign";
    await runSavedEmailAction(
      (savedEmail) => onSchedule(savedEmail),
      scheduledAtUtcMilli
        ? {
            message: buildEmailScheduledSuccessMessage({
              name,
              scheduledAtUtcMilli,
              timeZone: email.timeZone,
            }),
            highlights: [name],
          }
        : { message: "Email scheduled." },
      "schedule your email",
      // A Draft save stores no send time, so the schedule uses the chosen one.
      (savedEmail) => ({
        ...savedEmail,
        scheduledDate: email.scheduledDate,
        scheduledTime: email.scheduledTime,
      })
    );
  };

  const cancelSchedule = async () => {
    if (!onCancelSchedule) return;
    await runMetadataAction({
      action: onCancelSchedule,
      mergeMetadata: (current, saved) => ({
        ...current,
        status: saved.status,
        scheduledDate: saved.scheduledDate,
        scheduledTime: saved.scheduledTime,
      }),
      successMessage: "Scheduled email canceled.",
      failureTask: "cancel the schedule for this email",
    });
  };

  const newsroomArticleName = (current: EmailFormState) =>
    current.subject.trim() || current.campaignName.trim() || "This communication";

  // Design (16): Publish to Newsroom publishes what is on the page. Unsaved
  // changes are saved first (a Sent email saves only its newsroom article).
  const saveBeforeNewsroomAction = async (): Promise<boolean> => {
    if (!hasUnsavedChanges && email.id) return true;
    if (isSentRecord) {
      if (!onUpdatePublishedNewsroom) return true;
      const saved = await runAction(() => onUpdatePublishedNewsroom(cloneEmailForm(latestEmailRef.current)), null);
      if (saved) latestEmailRef.current = saved;
      return Boolean(saved);
    }
    const saved = await runAction(() => onSubmit(cloneEmailForm(latestEmailRef.current)), null);
    if (saved) latestEmailRef.current = saved;
    return Boolean(saved);
  };

  const publishNewsroom = async () => {
    if (!onPublishNewsroom || !(await saveBeforeNewsroomAction())) return;
    await runMetadataAction({
      action: onPublishNewsroom,
      mergeMetadata: mergeNewsroomMetadata,
      failureTask: "publish this email to your newsroom",
      successMessage: (saved) => {
        const name = newsroomArticleName(saved);
        const stamp = formatNewsroomStamp(saved.newsroomPublishedAtUtcMilli || Date.now(), saved.timeZone);
        return { message: `${name} has been successfully published to your newsroom on ${stamp}.`, highlights: [name, stamp] };
      },
    });
  };

  const unpublishNewsroom = async () => {
    if (!onUnpublishNewsroom) return;
    await runMetadataAction({
      action: onUnpublishNewsroom,
      mergeMetadata: mergeNewsroomMetadata,
      failureTask: "remove this email from your newsroom",
      successMessage: (saved) => {
        const name = newsroomArticleName(saved);
        const stamp = formatNewsroomStamp(saved.newsroomUnpublishedAtUtcMilli || Date.now(), saved.timeZone);
        return { message: `${name} has been successfully unpublished from your newsroom on ${stamp}.`, highlights: [name, stamp] };
      },
    });
  };

  // Design (16) edit-mode footer button: one label and message per status.
  const updateAction = mode !== "update" ? null
    : email.status === "draft" || email.status === "review"
      ? { label: "Update Draft", message: "Your email draft has been updated successfully!" }
      : email.status === "scheduled"
        ? { label: "Update Scheduled Email", message: "Your scheduled email has been updated successfully!" }
        : email.status === "sent" && onUpdatePublishedNewsroom
          ? {
            label: "Update Published Email",
            // Design (16) text when the article is in the newsroom; a sent email
            // that is not published keeps its changes for the next publish.
            message: email.newsroomStatus === "published"
              ? "Your published email has been updated successfully. The changes are now live in your newsroom."
              : "Your published email has been updated successfully. Click Publish to Newsroom to show the changes in your newsroom.",
          }
          : null;
  const runUpdateAction = async () => {
    if (!updateAction || isBusy) return;
    const emailToSave = cloneEmailForm(latestEmailRef.current);
    await runAction(
      () => email.status === "sent" && onUpdatePublishedNewsroom ? onUpdatePublishedNewsroom(emailToSave) : onSubmit(emailToSave),
      { message: updateAction.message }
    );
  };

  const renderActiveStep = () => {
    if (step === 1)
      return (
        <StepOneAudience
          email={email}
          recipientGroups={recipientGroups}
          memberTypeOptions={memberTypeOptions}
          audienceFilters={email.memberFilters}
          intendedRecipientCount={intendedRecipientCount}
          isAudiencePreviewLoading={isAudiencePreviewLoading}
          audiencePreviewError={currentAudiencePreviewError}
          isBusy={isBusy}
          isSentRecord={isSentRecord}
          toggleRecipientSource={toggleRecipientSource}
          toggleRecipientGroup={toggleRecipientGroup}
          archiveRecipientGroup={archiveRecipientGroup}
          updateAudienceFilter={updateAudienceFilter}
          clearAllFilters={clearAllFilters}
          updateEmailField={updateEmailField}
          recipientPreview={previewSummary}
          onRefreshRecipients={() => {
            latestAudiencePreviewRequestIdRef.current += 1;
            setAudiencePreviewResult(null);
            setAudiencePreviewError(null);
            setAudienceRefreshRevision((revision) => revision + 1);
          }}
        />
      );
    if (step === 2)
      return (
        <StepTwoDetails
          email={email}
          isBusy={isBusy}
          isSentRecord={isSentRecord}
          updateEmailField={updateEmailField}
          updateBuilderState={updateBuilderState}
          uploadImage={uploadBuilderImage}
          footerProfile={chamber}
          senderEmailOptions={senderEmailOptions}
        />
      );
    if (step === 3 && email.builderState.preservedDocument) {
      return <Alert severity="warning">This saved email uses content that this editor cannot change. Its original content is preserved. You can still change the campaign details and audience.</Alert>;
    }
    if (step === 3)
      return (
        <StepThreeContent
          onBuilderBackChange={onBuilderBackChange}
          memberOptions={memberOptions}
          memberOptionsLoading={memberOptionsLoading}
          onUndo={undoBuilder}
          onRedo={redoBuilder}
          canUndo={builderPastRef.current.length > 0}
          canRedo={builderFutureRef.current.length > 0}
          onHistoryTransactionStart={startBuilderTransaction}
          onHistoryTransactionEnd={endBuilderTransaction}
          email={email}
          isBusy={isBusy}
          isSentRecord={isSentRecord}
          updateBuilderState={updateBuilderState}
          uploadImage={uploadBuilderImage}
          uploadPdf={uploadBuilderPdf}
          updateEmailField={updateEmailField}
          footerProfile={chamber}
        />
      );
    return (
      <StepFourReview
        mode={mode}
        email={reviewEmail}
        isBusy={isBusy}
        isSendNowLoading={sendNowLoading}
        isSentRecord={isSentRecord}
        canRunSavedBackendAction={canRunSavedBackendAction}
        intendedRecipientCount={intendedRecipientCount}
        directRecipientCount={directRecipientCount}
        audienceRows={audienceSummaryRows}
        recipientPreview={previewSummary}
        isAudiencePreviewLoading={isAudiencePreviewLoading}
        audiencePreviewError={currentAudiencePreviewError}
        goToCampaignDetails={() => goToStep(2)}
        updateEmailField={updateEmailField}
        uploadImage={uploadBuilderImage}
        addAttachment={addAttachment}
        onDuplicateAsDraft={onDuplicateAsDraft}
        previewTestSend={previewTestSend}
        sendReview={sendReview}
        scheduleEmail={scheduleEmail}
        cancelSchedule={cancelSchedule}
        publishNewsroom={publishNewsroom}
        unpublishNewsroom={unpublishNewsroom}
        sendNow={sendNow}
        hasSendReviewAction={!!onPreviewTestSend && !!onSendReview}
        hasScheduleAction={!!onSchedule}
        hasCancelScheduleAction={!!onCancelSchedule}
        hasPublishNewsroomAction={!!onPublishNewsroom}
        hasUnpublishNewsroomAction={!!onUnpublishNewsroom}
        hasSendNowAction={!!onSendNow}
        footerProfile={chamber}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="rounded-md border border-gray-200 bg-white px-5 py-4 text-sm font-semibold text-gray-600">
        Loading your email...
      </div>
    );
  }

  return (
    <EmailDesignScope page>
      <FormPageShell
        headingId={`email-form-step-${step}`}
        heading={steps[step - 1].title}
        beforeMain={
          <UnsavedChangesPrompt
            // Stays on the "save" pop-up until Wave B adds the design Update
            // Draft / Update Scheduled / Update Published buttons on every
            // step (Monday 13232498622): edit mode has no other save today.
            when={hasUnsavedChanges || activeAssetUploadCount > 0}
            saving={isBusy}
            onSave={() => isSentRecord && onUpdatePublishedNewsroom
              ? runAction(() => onUpdatePublishedNewsroom(cloneEmailForm(latestEmailRef.current)), null).then(Boolean)
              : saveDraft(false)}
            saveError={notice?.severity === "error" ? applyCmpOrganizationNoun(notice.message, noun) : undefined}
          />
        }
        stepper={
          <div
            className="relative md:sticky top-0 z-[60] flex flex-col gap-6 bg-white pt-3 before:pointer-events-none before:absolute before:left-0 before:right-0 before:-top-8 before:h-8 before:bg-white before:content-[''] md:before:-left-10 md:before:-right-10"
            data-testid="email-wizard-sticky-header"
          >
            <div className="relative flex flex-wrap items-start justify-between gap-6 border-b border-[#ECECEC] pb-4">
              <div className="flex min-w-0 flex-1 flex-col">
                {mode === "update" ? (
                  <Link
                    to={RoutePaths.chamberAdmin_emails_manage}
                    className="mb-[14px] inline-flex w-fit items-center gap-2 rounded-lg border border-[#C7C7C7] bg-white px-[14px] py-[9px] font-['DM_Sans'] text-[13px] font-semibold leading-[normal] text-[#0A0A0A] hover:bg-[#F7F7F8]"
                  >
                    <ArrowBackIcon sx={{ fontSize: 13 }} />
                    Back to Manage Emails
                  </Link>
                ) : null}
                <h1
                  className={pageTitleClassName.replace(
                    "font-semibold",
                    "font-bold"
                  )}
                >
                  {pageTitle}
                </h1>
                <p
                  className={pageDescriptionClassName.replace(
                    "mt-2",
                    "mt-[6px]"
                  ).replace("leading-[1.55]", "leading-[normal]")}
                >
                  {pageDescription}
                </p>
              </div>
            </div>
            <StepProgress
              activeStep={step}
              steps={steps}
              ariaLabel={
                mode === "create" ? "Create email steps" : "Update email steps"
              }
              desktopGridColsClassName="lg:grid-cols-4"
              stepTestIdPrefix="email-step"
              variant="email"
              // Shipped Emails behavior kept: the stepper is clickable in BOTH
              // create and update modes (variance vs Members' create-mode static
              // stepper, logged in the B10 audit); goToStep validates every
              // intermediate step so forward jumps cannot skip a required step.
              onStepClick={(stepNumber) => goToStep(stepNumber as WizardStep)}
            />
          </div>
        }
        alerts={
          <>
            {notice ? (
              <Alert
                ref={noticeRef}
                severity={notice.severity}
                className="mb-6"
                onClose={() => setNotice(null)}
              >
                {/* Route handlers and mappers throw default wording. */}
                {applyCmpOrganizationNoun(notice.message, noun)}
              </Alert>
            ) : null}
            {categoriesError ? (
              <Alert severity="warning" className="mb-6">
                {getUserFacingErrorText(
                  categoriesError,
                  buildStaffErrorFallback(
                    "load the industry categories",
                    "Industry names may be missing from the audience summary."
                  )
                )}
              </Alert>
            ) : null}
            <ErrorBanner messages={Object.values(errors)} className="mb-6" />
          </>
        }
        actionBarAlert={
          // SH-006 dual-position pattern: the validation banner renders above
          // the step content AND above the footer button row.
          <ErrorBanner messages={Object.values(errors)} className="mb-4" />
        }
        actionBar={
          <>
            {step > 1 ? (
              <button
                type="button"
                className={`${secondaryButtonClassName} !min-h-[45px] !py-3 !leading-[normal]`}
                onClick={() => step === 3 && builderBackRef.current ? builderBackRef.current() : goToStep((step - 1) as WizardStep)}
                disabled={isBusy}
              >
                <ChevronLeftIcon sx={{ fontSize: 15 }} />
                Previous Step
              </button>
            ) : <span />}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              {/* Design (D:1427): Save Draft is a create-mode action only. */}
              {mode === "create" && !isSentRecord ? (
                <button
                  type="button"
                  className={`${darkButtonClassName} !border-0 !py-3 !leading-[normal]`}
                  onClick={() => void saveDraft()}
                  disabled={isBusy}
                >
                  Save Draft
                </button>
              ) : null}
              {updateAction ? (
                <button
                  type="button"
                  className={`${darkButtonClassName} !border-0 !py-3 !leading-[normal]`}
                  onClick={() => void runUpdateAction()}
                  disabled={isBusy}
                >
                  {updateAction.label}
                </button>
              ) : null}
              {step < 4 ? (
                <button
                  type="button"
                  className={`${primaryButtonClassName} !gap-[10px] !border-0 !px-6 !py-3 !leading-[normal]`}
                  onClick={() => goToStep((step + 1) as WizardStep)}
                  disabled={isBusy}
                >
                  Next Step: {steps[step].title}
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </button>
              ) : null}
            </div>
          </>
        }
        mainClassName="w-full [&+div]:mt-4 [&+div]:border-0 [&+div]:pt-0"
      >
        {renderActiveStep()}
        {/* The design success copy says "approved and sent to N recipients".
            A queued send is not yet delivered, so this keeps the confirmed
            queued state and the redirect to delivery status. */}
        <ActionSuccessDialog
          message={
            sendSuccessOpen
              ? "Your email is now sending. We are taking you to Manage Emails & Communications, where you can see its delivery status."
              : null
          }
          onClose={handleSendSuccessRedirect}
        />
        <ActionSuccessDialog
          message={sendSuccessOpen ? null : success?.message}
          highlights={success?.highlights}
          onClose={() => setSuccess(null)}
        />
      </FormPageShell>
    </EmailDesignScope>
  );
};

export default EmailForm;
